import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageHeader } from '../components/PageHeader';
import { MembershipCard } from '../components/MembershipCard';
import { useAuth } from '../contexts/AuthContext';
import { Award, Tag, Calendar, Coins, ChevronRight, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MemberPage() {
    const navigate = useNavigate();
    const { user, profile, token, refreshProfile } = useAuth();
    const [creditHistory, setCreditHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [token]);

    const fetchData = async () => {
        try {
            const response = await axios.get(`${API}/member/credit-history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCreditHistory(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const menuItems = [
        { icon: Tag, label: 'Member Offers', subtitle: 'Exclusive deals for you', path: '/member/offers' },
        { icon: Award, label: 'Points History', subtitle: 'View your transactions', path: '/member/points' },
    ];

    // Only show residents membership option for residence users, not shoppers
    const isResident = user?.user_type === 'residence';
    
    if (isResident) {
        if (profile?.membership_status === 'active') {
            menuItems.unshift({
                icon: Star,
                label: 'Residents Club',
                subtitle: `${profile.credits_balance || 0} credits available`,
                path: '/residents/dashboard',
                highlight: true
            });
        } else {
            menuItems.unshift({
                icon: Star,
                label: 'Join Residents Club',
                subtitle: '£995/year • 30 credits included',
                path: '/residents-club',
                highlight: true
            });
        }
    }

    return (
        <div className="page-container" data-testid="member-page">
            <PageHeader title="My Account" showBack />
            
            <div className="px-4 py-4">
                {/* Membership Card */}
                <MembershipCard user={user} profile={profile} />

                {/* Credits Info for Active Members */}
                {profile?.membership_status === 'active' && (
                    <div className="card-base bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 mt-6" data-testid="credits-info">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Coins className="w-8 h-8 text-amber-600" />
                                <div>
                                    <p className="font-bold text-stone-900">{profile.credits_balance || 0} Credits</p>
                                    <p className="text-amber-700 text-sm">Available to book activities</p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/residents/credits')}
                                className="text-amber-700 font-medium text-sm"
                            >
                                Buy more →
                            </button>
                        </div>
                    </div>
                )}

                {/* Quick Links */}
                <div className="mt-6 space-y-3" data-testid="member-menu">
                    {menuItems.map((item, index) => (
                        <button
                            key={index}
                            onClick={() => navigate(item.path)}
                            className={`w-full card-interactive flex items-center justify-between ${
                                item.highlight ? 'residents-card border-2' : ''
                            }`}
                            data-testid={`menu-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                    item.highlight ? 'bg-emerald-700' : 'bg-primary/10'
                                }`}>
                                    <item.icon className={`w-6 h-6 ${item.highlight ? 'text-white' : 'text-primary'}`} />
                                </div>
                                <div className="text-left">
                                    <p className="font-bold text-foreground">{item.label}</p>
                                    <p className="text-muted-foreground text-sm">{item.subtitle}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-muted-foreground" />
                        </button>
                    ))}
                </div>

                {/* Recent Credit Activity */}
                {profile?.membership_status === 'active' && creditHistory.length > 0 && (
                    <div className="mt-8">
                        <h2 className="text-lg font-bold text-foreground mb-4">Recent Credit Activity</h2>
                        
                        <div className="card-base divide-y divide-primary/10" data-testid="credit-history">
                            {creditHistory.slice(0, 5).map((tx) => (
                                <div key={tx.id} className="py-3 first:pt-0 last:pb-0">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="font-medium text-foreground">{tx.description}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(tx.created_at).toLocaleDateString('en-GB', {
                                                    day: 'numeric',
                                                    month: 'short'
                                                })}
                                            </p>
                                        </div>
                                        <span className={`font-bold ${
                                            tx.amount > 0 ? 'text-emerald-600' : 'text-stone-600'
                                        }`}>
                                            {tx.amount > 0 ? '+' : ''}{tx.amount}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MemberPage;
